import type { ReactNode } from "react";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface EntityGeometrySectionProps<T> {
  addNameValue: string;
  onAddNameChange: (value: string) => void;
  addGeometryIdValue: string;
  onAddGeometryIdChange: (value: string) => void;
  onAdd: () => void;
  addPending?: boolean;
  isLoading?: boolean;
  items: T[];
  getKey: (item: T) => number;
  renderName: (item: T) => string;
  renderGeometryId: (item: T) => number | null | undefined;
  /** Компактная сводка слева от имени (например swatches стиля карты). */
  renderStylePreview?: (item: T) => ReactNode;
  onFocus: (item: T) => void;
  onDelete: (item: T) => void;
  deletePending?: boolean;
  emptyText?: string;
  /** Ключ выбранной строки (для стиля / sync с картой). */
  selectedKey?: number | null;
  onSelect?: (item: T) => void;
}

export function EntityGeometrySection<T>({
  addNameValue,
  onAddNameChange,
  addGeometryIdValue,
  onAddGeometryIdChange,
  onAdd,
  addPending = false,
  isLoading = false,
  items,
  getKey,
  renderName,
  renderGeometryId,
  renderStylePreview,
  onFocus,
  onDelete,
  deletePending = false,
  emptyText = "Геометрии не привязаны",
  selectedKey = null,
  onSelect,
}: EntityGeometrySectionProps<T>) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-[1fr_220px_auto]">
        <Input
          placeholder="Наименование геометрии"
          value={addNameValue}
          onChange={(e) => onAddNameChange(e.target.value)}
        />
        <Input
          placeholder="ID геометрии (core.geometries.id)"
          value={addGeometryIdValue}
          onChange={(e) => onAddGeometryIdChange(e.target.value)}
        />
        <Button onClick={onAdd} disabled={addPending}>
          Добавить
        </Button>
      </div>

      <div className="border rounded-md">
        {isLoading ? (
          <div className="p-3">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground">{emptyText}</div>
        ) : (
          items.map((item) => {
            const geometryId = renderGeometryId(item);
            const key = getKey(item);
            const selected = selectedKey != null && selectedKey === key;
            return (
              <div
                key={key}
                className={cn(
                  "grid grid-cols-[minmax(0,1fr)_minmax(0,9rem)_auto_auto] gap-2 px-3 py-2 border-b text-sm items-center",
                  selected && "bg-amber-50 dark:bg-amber-950/30",
                )}
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  {renderStylePreview?.(item)}
                  {onSelect ? (
                    <button
                      type="button"
                      className="min-w-0 truncate text-left cursor-pointer hover:underline"
                      onClick={() => onSelect(item)}
                    >
                      {renderName(item)}
                    </button>
                  ) : (
                    <span className="min-w-0 truncate">{renderName(item)}</span>
                  )}
                </div>
                {onSelect ? (
                  <button
                    type="button"
                    className="truncate text-left text-muted-foreground cursor-pointer hover:underline"
                    onClick={() => onSelect(item)}
                  >
                    geometry_id: {geometryId ?? "-"}
                  </button>
                ) : (
                  <span className="truncate text-muted-foreground">geometry_id: {geometryId ?? "-"}</span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onFocus(item)}
                  disabled={geometryId == null}
                  title="Показать на карте"
                >
                  <Eye className="mr-1 h-3.5 w-3.5" />
                  На карте
                </Button>
                <Button size="sm" variant="destructive" onClick={() => onDelete(item)} disabled={deletePending}>
                  Удалить
                </Button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
